import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Landmark, Languages } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(password);
    
    if (result.success) {
      toast.success(t('login') + ' successful!');
      navigate('/');
    } else {
      toast.error(result.error || 'Invalid credentials');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="flex h-screen">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1597058712635-3182d1eacc1e?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-primary/80 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <Landmark className="h-24 w-24 mx-auto mb-6" />
            <h1 className={`text-4xl font-bold mb-4 ${language === 'mr' ? 'marathi-text' : ''}`}>
              {language === 'en' ? 'Nagpur Municipal Corporation' : 'नागपूर महानगरपालिका'}
            </h1>
            <p className={`text-xl ${language === 'mr' ? 'marathi-text' : ''}`}>
              {language === 'en' ? 'General Election 2025-26' : 'सामान्य निवडणूक 2025-26'}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Landmark className="h-16 w-16 mx-auto text-primary mb-4" />
            <h2 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
              {t('adminLogin')}
            </h2>
            <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
              {t('appTitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="password" className={language === 'mr' ? 'marathi-text' : ''}>
                {t('password')}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder={t('enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className={language === 'mr' ? 'marathi-text' : ''}
              />
            </div>

            <Button
              type="submit"
              className={`w-full ${language === 'mr' ? 'marathi-text' : ''}`}
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? t('loading') : t('login')}
            </Button>
          </form>

          <div className="text-center">
            <Button
              variant="outline"
              onClick={toggleLanguage}
              className={`gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}
              data-testid="lang-toggle-btn"
            >
              <Languages className="h-4 w-4" />
              {language === 'en' ? 'मराठी' : 'English'}
            </Button>
          </div>

          <p className={`text-xs text-center text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
            {language === 'en' ? 'Contact IT department for password' : 'पासवर्डसाठी आयटी विभागाशी संपर्क साधा'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;